import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import AnalyticsRouterHook from "@/components/AnalyticsRouterHook";
import ScrollToHash from "@/components/ScrollToHash";
import { initGA4 } from "@/lib/analytics";
import Home from "@/pages/Home";
import HR from "@/pages/HR";
import Continuum from "@/pages/Continuum";
import Academy from "@/pages/Academy";
import ProductDetail from "@/pages/ProductDetail";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CoursePlayer from "@/pages/CoursePlayer";
import Book from "@/pages/Book";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import About from "@/pages/About";
import Solutions from "@/pages/Solutions";
import WhoWeServe from "@/pages/WhoWeServe";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Press from "@/pages/Press";
import Tools from "@/pages/Tools";
import CourseViewer from "@/pages/CourseViewer";
import ContinuumPortal, { CurriculumDetail } from "@/pages/ContinuumPortal";
import PortalPageWrapper from "@/components/PortalPageWrapper";

// Portal ported pages
import PortalOverview from "@/pages/portal/Overview";
import PortalRoles from "@/pages/portal/Roles";
import PortalLevelsOfCare from "@/pages/portal/LevelsOfCare";
import PortalPositioning from "@/pages/portal/Positioning";
import PortalPricing from "@/pages/portal/Pricing";
import PortalReferenceCard from "@/pages/portal/ReferenceCard";
import PortalFrameworks from "@/pages/portal/Frameworks";
import PortalAskPlaybook from "@/pages/portal/AskPlaybook";

// Portal tools
import ToolsIndex from "@/pages/portal/tools/ToolsIndex";
import ToolPHQ9 from "@/pages/portal/tools/PHQ9";
import ToolGAD7 from "@/pages/portal/tools/GAD7";
import ToolORS from "@/pages/portal/tools/ORS";
import ToolSRS from "@/pages/portal/tools/SRS";
import ToolASAM from "@/pages/portal/tools/ASAMScorer";
import ToolCHIME from "@/pages/portal/tools/CHIMEAudit";
import ToolFrameworkMap from "@/pages/portal/tools/FrameworkMap";
import ToolSAFET from "@/pages/portal/tools/SAFET";
import ToolMSE from "@/pages/portal/tools/MSE";
import ToolCognitive from "@/pages/portal/tools/CognitiveScreen";
import ToolStaffing from "@/pages/portal/tools/Staffing";
import ToolReading from "@/pages/portal/tools/ReadingLevel";
import ToolScreening from "@/pages/portal/tools/ScreeningBattery";
import ToolDischarge from "@/pages/portal/tools/DischargeReadiness";
import ToolHistory from "@/pages/portal/tools/History";

import { detectSubdomain } from "@/lib/subdomain";

function App() {
  const sub = detectSubdomain();
  useEffect(() => { initGA4(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsRouterHook />
        <ScrollToHash />
        <AuthProvider>
          {sub === "hr" ? <HrBranchRoutes /> :
           sub === "continuum" ? <ContinuumBranchRoutes /> :
           <MainRoutes />}
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

const SHARED_ROUTES = (
  <>
    <Route path="/academy" element={<Academy />} />
    <Route path="/academy/:id" element={<ProductDetail />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/press" element={<Press />} />
    <Route path="/tools" element={<Tools />} />
    <Route path="/course/:productId" element={<CourseViewer />} />
    <Route path="/academy/:productId/learn" element={<CoursePlayer />} />
    <Route path="/checkout/success" element={<CheckoutSuccess />} />
    <Route path="/book" element={<Book />} />
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/admin" element={<Admin />} />
    <Route path="/admin/login" element={<AdminLogin />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/portal/continuum" element={<ContinuumPortal />} />
    <Route path="/portal/continuum/curricula/:id" element={<CurriculumDetail />} />

    {/* Ported portal pages (gated behind continuum access) */}
    <Route path="/portal/continuum/overview" element={<PortalPageWrapper><PortalOverview /></PortalPageWrapper>} />
    <Route path="/portal/continuum/roles" element={<PortalPageWrapper><PortalRoles /></PortalPageWrapper>} />
    <Route path="/portal/continuum/levels-of-care" element={<PortalPageWrapper><PortalLevelsOfCare /></PortalPageWrapper>} />
    <Route path="/portal/continuum/positioning" element={<PortalPageWrapper><PortalPositioning /></PortalPageWrapper>} />
    <Route path="/portal/continuum/pricing" element={<PortalPageWrapper><PortalPricing /></PortalPageWrapper>} />
    <Route path="/portal/continuum/reference-card" element={<PortalPageWrapper><PortalReferenceCard /></PortalPageWrapper>} />
    <Route path="/portal/continuum/frameworks" element={<PortalPageWrapper><PortalFrameworks /></PortalPageWrapper>} />
    <Route path="/portal/continuum/ask-playbook" element={<PortalPageWrapper><PortalAskPlaybook /></PortalPageWrapper>} />

    {/* Ported tools */}
    <Route path="/portal/continuum/tools" element={<PortalPageWrapper><ToolsIndex /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/phq9" element={<PortalPageWrapper><ToolPHQ9 /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/gad7" element={<PortalPageWrapper><ToolGAD7 /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/ors" element={<PortalPageWrapper><ToolORS /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/srs" element={<PortalPageWrapper><ToolSRS /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/asam" element={<PortalPageWrapper><ToolASAM /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/chime" element={<PortalPageWrapper><ToolCHIME /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/framework-map" element={<PortalPageWrapper><ToolFrameworkMap /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/safet" element={<PortalPageWrapper><ToolSAFET /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/mse" element={<PortalPageWrapper><ToolMSE /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/cognitive" element={<PortalPageWrapper><ToolCognitive /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/staffing" element={<PortalPageWrapper><ToolStaffing /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/reading-level" element={<PortalPageWrapper><ToolReading /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/screening" element={<PortalPageWrapper><ToolScreening /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/discharge" element={<PortalPageWrapper><ToolDischarge /></PortalPageWrapper>} />
    <Route path="/portal/continuum/tools/history" element={<PortalPageWrapper><ToolHistory /></PortalPageWrapper>} />
  </>
);

function MainRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/hr" element={<HR />} />
      <Route path="/continuum" element={<Continuum />} />
      <Route path="/about" element={<About />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/who-we-serve" element={<WhoWeServe />} />
      {SHARED_ROUTES}
    </Routes>
  );
}

function HrBranchRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HR />} />
      {SHARED_ROUTES}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ContinuumBranchRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Continuum />} />
      {SHARED_ROUTES}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
